import { COLLISION_CONFIG } from '../constants.js';
import { checkCompoundOverlap, transformCircleToWorld } from '../collisionShapes.js';
import type { CollisionCircle } from '../collisionShapes.js';
import type { Collidable, Stunnable } from './Collidable.js';
import type { Damageable } from './Damageable.js';
import type { SpaceEntity } from './SpaceEntity.js';
import type { CollisionResult, DamageResult, SelectionInfo } from './types.js';

// =============================================================================
// COLLISION UTILITIES
// =============================================================================

/**
 * Minimal physics-only interface for collision calculations.
 * Both client entities and server states satisfy this via structural typing.
 */
export interface CollisionBody {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  collisionRadius: number;
  mass: number;
}

/**
 * Check if two collidable entities are overlapping.
 */
export function checkCollision(a: Collidable, b: Collidable): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distSq = dx * dx + dy * dy;
  const minDist = a.collisionRadius + b.collisionRadius;
  return distSq <= minDist * minDist;
}

/**
 * Get effective mass for collision calculations.
 * Compresses the mass range so heavy ships still bounce visibly.
 */
export function getEffectiveMass(mass: number): number {
  const minMass = 70;
  const maxMass = 600;
  const compressedMax = COLLISION_CONFIG.MASS_COMPRESSION_MAX;

  if (mass <= minMass) return minMass;
  if (mass >= maxMass) return compressedMax;

  const ratio = (mass - minMass) / (maxMass - minMass);
  return minMass + ratio * (compressedMax - minMass);
}

/**
 * Ensure minimum bounce for heavy entities.
 * Prevents heavy ships from feeling immovable.
 */
function enforceMinBounce(
  oldVx: number, oldVy: number,
  newVel: { x: number; y: number },
  impactSpeed: number
): { x: number; y: number } {
  const minBounce = impactSpeed * COLLISION_CONFIG.MIN_BOUNCE_FACTOR;
  const cx = newVel.x - oldVx;
  const cy = newVel.y - oldVy;
  const changeSpeed = Math.sqrt(cx * cx + cy * cy);

  if (changeSpeed < minBounce && changeSpeed > 0) {
    const scale = minBounce / changeSpeed;
    return { x: oldVx + cx * scale, y: oldVy + cy * scale };
  }
  return newVel;
}

/**
 * THE collision physics function. Single source of truth for both client and server.
 *
 * Handles:
 * - Ship vs asteroid/comet: reflection bounce (v' = v - 2*(v·n)*n)
 * - Ship vs ship: Newton's impulse with mass compression
 * - Overlap without enough speed: gentle separation push
 * - Minimum bounce enforcement for heavy ships
 * - Proportional penetration resolution (separation offsets)
 *
 * @returns null if no overlap, otherwise velocities + separation offsets
 */
export function calculateCollision(
  a: CollisionBody,
  b: CollisionBody,
  aIsAsteroid: boolean = false,
  bIsAsteroid: boolean = false
): CollisionResult | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const minDist = a.collisionRadius + b.collisionRadius;

  if (distance > minDist || distance === 0) return null;

  // Collision normal (from A to B)
  const nx = dx / distance;
  const ny = dy / distance;

  // Relative velocity along collision normal
  const dvx = a.velocityX - b.velocityX;
  const dvy = a.velocityY - b.velocityY;
  const dvn = dvx * nx + dvy * ny;

  const impactSpeed = Math.abs(dvn);
  // dvn > 0 = A approaching B (relative velocity aligned with A→B normal)
  // dvn < 0 = A moving away from B
  const isValidImpact = dvn > 0 && impactSpeed >= COLLISION_CONFIG.MIN_IMPACT_SPEED;

  // Get effective masses
  const massA = aIsAsteroid ? Infinity : getEffectiveMass(a.mass);
  const massB = bIsAsteroid ? Infinity : getEffectiveMass(b.mass);

  let newVelocityA = { x: a.velocityX, y: a.velocityY };
  let newVelocityB = { x: b.velocityX, y: b.velocityY };

  if (isValidImpact) {
    if (aIsAsteroid && bIsAsteroid) {
      // Both infinite mass — no movement
    } else if (aIsAsteroid) {
      // A is obstacle — only B bounces
      // Reflection with restitution: v' = v + (1+e)*dvn*n
      const e = COLLISION_CONFIG.ASTEROID_BOUNCE_MULTIPLIER;
      newVelocityB = {
        x: b.velocityX + (1 + e) * dvn * nx,
        y: b.velocityY + (1 + e) * dvn * ny,
      };
      newVelocityB = enforceMinBounce(b.velocityX, b.velocityY, newVelocityB, impactSpeed);
    } else if (bIsAsteroid) {
      // B is obstacle — only A bounces
      const e = COLLISION_CONFIG.ASTEROID_BOUNCE_MULTIPLIER;
      newVelocityA = {
        x: a.velocityX - (1 + e) * dvn * nx,
        y: a.velocityY - (1 + e) * dvn * ny,
      };
      newVelocityA = enforceMinBounce(a.velocityX, a.velocityY, newVelocityA, impactSpeed);
    } else {
      // Ship vs Ship — Newton's impulse with mass compression
      const impulse = (-(1 + COLLISION_CONFIG.RESTITUTION) * dvn) / (1 / massA + 1 / massB);

      newVelocityA = {
        x: a.velocityX + (impulse / massA) * nx,
        y: a.velocityY + (impulse / massA) * ny,
      };
      newVelocityB = {
        x: b.velocityX - (impulse / massB) * nx,
        y: b.velocityY - (impulse / massB) * ny,
      };

      newVelocityA = enforceMinBounce(a.velocityX, a.velocityY, newVelocityA, impactSpeed);
      newVelocityB = enforceMinBounce(b.velocityX, b.velocityY, newVelocityB, impactSpeed);
    }
  } else {
    // Overlapping but not enough speed for full collision.
    // Gentle separation push proportional to penetration depth.
    const penetration = minDist - distance;
    const separationSpeed = Math.max(50, penetration * 2);

    if (!aIsAsteroid) {
      newVelocityA = {
        x: a.velocityX - nx * separationSpeed,
        y: a.velocityY - ny * separationSpeed,
      };
    }
    if (!bIsAsteroid) {
      newVelocityB = {
        x: b.velocityX + nx * separationSpeed,
        y: b.velocityY + ny * separationSpeed,
      };
    }
  }

  // Proportional penetration resolution — separate bodies immediately.
  // Move each body proportional to INVERSE mass (lighter moves more).
  const penetration = minDist - distance;
  let separationA = { x: 0, y: 0 };
  let separationB = { x: 0, y: 0 };

  if (penetration > 0) {
    if (aIsAsteroid) {
      // A is immovable — B takes all separation
      separationB = { x: nx * penetration, y: ny * penetration };
    } else if (bIsAsteroid) {
      // B is immovable — A takes all separation
      separationA = { x: -nx * penetration, y: -ny * penetration };
    } else {
      // Both movable — split proportional to inverse mass
      const totalInvMass = 1 / massA + 1 / massB;
      const ratioA = (1 / massA) / totalInvMass;
      const ratioB = (1 / massB) / totalInvMass;
      separationA = { x: -nx * penetration * ratioA, y: -ny * penetration * ratioA };
      separationB = { x: nx * penetration * ratioB, y: ny * penetration * ratioB };
    }
  }

  return {
    collided: true,
    impactSpeed: isValidImpact ? impactSpeed : Math.max(50, penetration * 2),
    impactAngle: Math.atan2(dy, dx),
    newVelocityA,
    newVelocityB,
    separationA,
    separationB,
    damageA: false,
    damageB: false,
    damageAmountA: 0,
    damageAmountB: 0,
  };
}

/**
 * Extended collision body that supports compound circle shapes.
 * If collisionShapes is provided, the shapes are used for narrow-phase detection.
 * The ship's center (x,y) and mass are still used for physics response.
 */
export interface CompoundCollisionBody extends CollisionBody {
  /** Compound shapes in local space (relative to ship center, +Y = forward) */
  collisionShapes?: CollisionCircle[];
  /** Ship heading in radians for rotating local shapes to world space */
  heading?: number;
}

/**
 * Calculate collision between two bodies that may have compound shapes.
 *
 * If either body has collisionShapes, we use compound circle detection:
 * 1. Find the deepest-penetrating circle pair between both shapes
 * 2. Use that pair's contact point as the collision normal
 * 3. Apply Newton's impulse using the ship centers and masses (unchanged physics)
 *
 * Falls back to single-circle calculateCollision() if neither has shapes.
 */
export function calculateCompoundCollision(
  a: CompoundCollisionBody,
  b: CompoundCollisionBody,
  aIsAsteroid: boolean = false,
  bIsAsteroid: boolean = false
): CollisionResult | null {
  const aHasShapes = a.collisionShapes && a.collisionShapes.length > 0;
  const bHasShapes = b.collisionShapes && b.collisionShapes.length > 0;

  // If neither has compound shapes, fall back to simple collision
  if (!aHasShapes && !bHasShapes) {
    return calculateCollision(a, b, aIsAsteroid, bIsAsteroid);
  }

  // Transform shapes to world space
  const worldA = aHasShapes
    ? a.collisionShapes!.map(c => transformCircleToWorld(c, a.x, a.y, a.heading || 0))
    : [{ x: a.x, y: a.y, r: a.collisionRadius }];

  const worldB = bHasShapes
    ? b.collisionShapes!.map(c => transformCircleToWorld(c, b.x, b.y, b.heading || 0))
    : [{ x: b.x, y: b.y, r: b.collisionRadius }];

  // Find deepest penetrating pair
  const overlap = checkCompoundOverlap(worldA, worldB);
  if (!overlap) return null;

  // Use the colliding circle pair's centers for the collision normal
  const cx = overlap.circleB.x - overlap.circleA.x;
  const cy = overlap.circleB.y - overlap.circleA.y;
  const contactDist = Math.sqrt(cx * cx + cy * cy);

  if (contactDist === 0) return null;

  const nx = cx / contactDist;
  const ny = cy / contactDist;

  // Relative velocity along collision normal (using ship centers' velocities)
  const dvx = a.velocityX - b.velocityX;
  const dvy = a.velocityY - b.velocityY;
  const dvn = dvx * nx + dvy * ny;

  const impactSpeed = Math.abs(dvn);
  const isValidImpact = dvn > 0 && impactSpeed >= COLLISION_CONFIG.MIN_IMPACT_SPEED;

  const massA = aIsAsteroid ? Infinity : getEffectiveMass(a.mass);
  const massB = bIsAsteroid ? Infinity : getEffectiveMass(b.mass);

  let newVelocityA = { x: a.velocityX, y: a.velocityY };
  let newVelocityB = { x: b.velocityX, y: b.velocityY };

  if (isValidImpact) {
    if (aIsAsteroid && bIsAsteroid) {
      // Both immovable
    } else if (aIsAsteroid) {
      const e = COLLISION_CONFIG.ASTEROID_BOUNCE_MULTIPLIER;
      newVelocityB = {
        x: b.velocityX + (1 + e) * dvn * nx,
        y: b.velocityY + (1 + e) * dvn * ny,
      };
      newVelocityB = enforceMinBounce(b.velocityX, b.velocityY, newVelocityB, impactSpeed);
    } else if (bIsAsteroid) {
      const e = COLLISION_CONFIG.ASTEROID_BOUNCE_MULTIPLIER;
      newVelocityA = {
        x: a.velocityX - (1 + e) * dvn * nx,
        y: a.velocityY - (1 + e) * dvn * ny,
      };
      newVelocityA = enforceMinBounce(a.velocityX, a.velocityY, newVelocityA, impactSpeed);
    } else {
      const impulse = (-(1 + COLLISION_CONFIG.RESTITUTION) * dvn) / (1 / massA + 1 / massB);
      newVelocityA = {
        x: a.velocityX + (impulse / massA) * nx,
        y: a.velocityY + (impulse / massA) * ny,
      };
      newVelocityB = {
        x: b.velocityX - (impulse / massB) * nx,
        y: b.velocityY - (impulse / massB) * ny,
      };
      newVelocityA = enforceMinBounce(a.velocityX, a.velocityY, newVelocityA, impactSpeed);
      newVelocityB = enforceMinBounce(b.velocityX, b.velocityY, newVelocityB, impactSpeed);
    }
  } else {
    // Gentle separation push
    const separationSpeed = Math.max(50, overlap.penetration * 2);
    if (!aIsAsteroid) {
      newVelocityA = {
        x: a.velocityX - nx * separationSpeed,
        y: a.velocityY - ny * separationSpeed,
      };
    }
    if (!bIsAsteroid) {
      newVelocityB = {
        x: b.velocityX + nx * separationSpeed,
        y: b.velocityY + ny * separationSpeed,
      };
    }
  }

  // Proportional separation using the contact normal
  let separationA = { x: 0, y: 0 };
  let separationB = { x: 0, y: 0 };
  const penetration = overlap.penetration;

  if (penetration > 0) {
    if (aIsAsteroid) {
      separationB = { x: nx * penetration, y: ny * penetration };
    } else if (bIsAsteroid) {
      separationA = { x: -nx * penetration, y: -ny * penetration };
    } else {
      const totalInvMass = 1 / massA + 1 / massB;
      const ratioA = (1 / massA) / totalInvMass;
      const ratioB = (1 / massB) / totalInvMass;
      separationA = { x: -nx * penetration * ratioA, y: -ny * penetration * ratioA };
      separationB = { x: nx * penetration * ratioB, y: ny * penetration * ratioB };
    }
  }

  return {
    collided: true,
    impactSpeed: isValidImpact ? impactSpeed : Math.max(50, penetration * 2),
    impactAngle: Math.atan2(cy, cx),
    newVelocityA,
    newVelocityB,
    separationA,
    separationB,
    damageA: false,
    damageB: false,
    damageAmountA: 0,
    damageAmountB: 0,
  };
}

/**
 * Apply collision result to an entity.
 * Updates velocity and optionally applies stun.
 */
export function applyCollisionToEntity(
  entity: Collidable & Partial<Stunnable>,
  newVelocity: { x: number; y: number },
  applyStun: boolean = true
): void {
  // Update velocity
  entity.velocityX = newVelocity.x;
  entity.velocityY = newVelocity.y;

  // Apply stun if entity supports it
  if (applyStun && 'isStunned' in entity && 'stunEndTime' in entity) {
    const stunnable = entity as Stunnable;
    // Don't reset stun timer if already stunned (per collisions.md)
    if (!stunnable.isStunned) {
      stunnable.isStunned = true;
      stunnable.stunEndTime = Date.now() + COLLISION_CONFIG.STUN_DURATION_MS;
    }
  }
}

/**
 * Update stun state for an entity.
 * Call this every frame to check if stun has expired.
 */
export function updateStunState(entity: Stunnable): void {
  if (entity.isStunned && Date.now() >= entity.stunEndTime) {
    entity.isStunned = false;
    entity.stunEndTime = 0;
  }
}

// =============================================================================
// DAMAGE UTILITIES
// =============================================================================

/**
 * Apply damage to a damageable entity.
 * Shield absorbs damage first, then hull.
 *
 * This is THE damage function - same logic for all damageable entities.
 *
 * @param entity - The entity to damage
 * @param damage - Amount of damage to apply
 * @returns Damage result with breakdown
 */
export function takeDamage(entity: Damageable, damage: number): DamageResult {
  let remainingDamage = damage;
  let shieldDamage = 0;
  let hullDamage = 0;

  // Shield absorbs first
  if (entity.shield > 0) {
    shieldDamage = Math.min(entity.shield, remainingDamage);
    entity.shield -= shieldDamage;
    remainingDamage -= shieldDamage;
  }

  // Hull takes the rest
  if (remainingDamage > 0) {
    hullDamage = Math.min(entity.hull, remainingDamage);
    entity.hull -= hullDamage;
  }

  return {
    shieldDamage,
    hullDamage,
    shieldRemaining: entity.shield,
    hullRemaining: entity.hull,
    destroyed: entity.hull <= 0,
  };
}

/**
 * Check if a damageable entity is dead (hull <= 0)
 */
export function isDead(entity: Damageable): boolean {
  return entity.hull <= 0;
}

/**
 * Heal an entity's hull (not above max)
 */
export function healHull(entity: Damageable, amount: number): number {
  const healed = Math.min(amount, entity.maxHull - entity.hull);
  entity.hull += healed;
  return healed;
}

/**
 * Recharge an entity's shield (not above max)
 */
export function rechargeShield(entity: Damageable, amount: number): number {
  const recharged = Math.min(amount, entity.maxShield - entity.shield);
  entity.shield += recharged;
  return recharged;
}

// =============================================================================
// SELECTION UTILITIES
// =============================================================================

/**
 * Check if a point is within selection range of an entity.
 * Standard implementation for all selectable entities.
 */
export function isPointInRange(
  entity: { x: number; y: number },
  worldX: number,
  worldY: number,
  radius: number
): boolean {
  const dx = worldX - entity.x;
  const dy = worldY - entity.y;
  return dx * dx + dy * dy <= radius * radius;
}

/**
 * Find the closest selectable entity at a point.
 */
export function findEntityAtPoint<T extends SpaceEntity>(
  entities: Iterable<T>,
  worldX: number,
  worldY: number
): { entity: T; info: SelectionInfo } | null {
  let closest: T | null = null;
  let closestDist = Infinity;

  for (const entity of entities) {
    if (entity.isPointInSelectionRange(worldX, worldY)) {
      const dx = worldX - entity.x;
      const dy = worldY - entity.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < closestDist) {
        closest = entity;
        closestDist = dist;
      }
    }
  }

  if (closest) {
    return { entity: closest, info: closest.getSelectionInfo() };
  }
  return null;
}
